import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiService } from '../../services/api';
import { Plus, Pencil, Trash2, Users, ChevronDown, ChevronUp, User, X, Camera, LayoutGrid, LayoutList } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // Prefer same-origin uploads path to avoid broken images behind reverse proxies.
  if (url.startsWith('/uploads/')) {
    return url;
  }

  const normalizedPath = url.startsWith('/') ? url : `/${url}`;
  const baseUrl = (BACKEND_URL || '').replace(/\/api\/?$/, '');
  if (!baseUrl) return normalizedPath;
  return `${baseUrl}${normalizedPath}`;
};

const FORMATIONS_BY_FORMAT = {
  '11v11': ['4-3-3', '4-4-2', '4-2-3-1', '3-5-2', '3-4-3', '5-3-2', '5-4-1'],
  '7v7': ['2-3-1', '3-2-1', '2-1-2-1', '3-1-2', '2-2-2'],
};
const PLAYER_FORMAT_META = {
  '11v11': { label: '11 vs 11' },
  '7v7': { label: '7 vs 7' },
};
const POSITIONS = [
  { value: 'GK', label: 'Kiper (GK)', color: 'bg-yellow-500' },
  { value: 'DEF', label: 'Belakang (DEF)', color: 'bg-blue-500' },
  { value: 'MID', label: 'Tengah (MID)', color: 'bg-green-500' },
  { value: 'FWD', label: 'Depan (FWD)', color: 'bg-red-500' },
];
const PLAYER_GENDERS = [
  { value: 'L', label: 'Laki-laki' },
  { value: 'P', label: 'Perempuan' },
];

const AdminParticipants = ({ participantType }) => {
  const [participants, setParticipants] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPlayersModal, setShowPlayersModal] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'card' | 'list'
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 15;
  const [detailPlayerModal, setDetailPlayerModal] = useState({ open: false, participantName: '', player: null });
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    participant_type: 'school',
    coach: '',
    assistant_coach: '',
    category: 'U-14',
    founded_year: new Date().getFullYear(),
    player_format: '11v11',
    formation: '4-3-3',
    logo: null,
    coach_photo: null,
    assistant_coach_photo: null
  });
  const [players, setPlayers] = useState([]);
  const [selectedPlayerFormat, setSelectedPlayerFormat] = useState('11v11');
  const [selectedFormation, setSelectedFormation] = useState('4-3-3');
  const [editingPlayerIndex, setEditingPlayerIndex] = useState(null);
  const [selectedPlayerIndex, setSelectedPlayerIndex] = useState(null);
  const [showAddPlayerForm, setShowAddPlayerForm] = useState(false);
  const [playerPhotoPreview, setPlayerPhotoPreview] = useState(null);
  const [coachPhotoPreview, setCoachPhotoPreview] = useState(null);
  const [assistantCoachPhotoPreview, setAssistantCoachPhotoPreview] = useState(null);
  const emptyPlayer = {
    name: '',
    number: '',
    position: 'MID',
    birth_place: '',
    birth_date: '',
    gender: '',
    school_origin: '',
    class_name: '',
    photo: '',
    birth_certificate_file: '',
    nisn_file: '',
    family_card_file: '',
    photo_upload: null,
    birth_certificate_upload: null,
    nisn_upload: null,
    family_card_upload: null,
  };
  const [newPlayer, setNewPlayer] = useState(emptyPlayer);
  const { toast } = useToast();
  const { isClubAdmin } = useAuth();

  const resetPlayerForm = () => {
    setNewPlayer(emptyPlayer);
    setEditingPlayerIndex(null);
    setPlayerPhotoPreview(null);
    setShowAddPlayerForm(false);
  };

  const normalizePlayer = (player = {}) => ({
    name: player.name || '',
    number: player.number || '',
    position: player.position || 'MID',
    birth_place: player.birth_place || '',
    birth_date: player.birth_date || '',
    gender: player.gender || '',
    school_origin: player.school_origin || '',
    class_name: player.class_name || '',
    photo: player.photo || '',
    birth_certificate_file: player.birth_certificate_file || '',
    nisn_file: player.nisn_file || '',
    family_card_file: player.family_card_file || '',
    photo_upload: null,
    birth_certificate_upload: null,
    nisn_upload: null,
    family_card_upload: null,
  });

  useEffect(() => {
    loadParticipants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantType]);

  const loadParticipants = async () => {
    setLoading(true);
    try {
      const params = participantType ? { participant_type: participantType } : {};
      const response = await apiService.admin.getParticipants(params);
      setParticipants(response.data || []);
      setCurrentPage(1);
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal memuat data peserta', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const normalizedName = (formData.name || '').trim();
      const duplicateName = participants.some((participant) => {
        if (editingParticipant && participant.id === editingParticipant.id) {
          return false;
        }
        return (participant.name || '').trim().toLowerCase() === normalizedName.toLowerCase();
      });
      if (duplicateName) {
        toast({ title: 'Error', description: 'Nama peserta sudah digunakan', variant: 'destructive' });
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append('name', normalizedName);
      formDataToSend.append('city', formData.city);
      formDataToSend.append('participant_type', formData.participant_type || 'school');
      formDataToSend.append('coach', formData.coach);
      formDataToSend.append('assistant_coach', formData.assistant_coach || '');
      formDataToSend.append('category', formData.category);
      if (Number.isFinite(Number(formData.founded_year))) {
        formDataToSend.append('founded_year', String(formData.founded_year));
      }
      formDataToSend.append('player_format', formData.player_format || '11v11');
      formDataToSend.append('formation', formData.formation);
      
      if (editingParticipant) {
        formDataToSend.append('players', JSON.stringify(editingParticipant.players || []));
      } else {
        formDataToSend.append('players', JSON.stringify([]));
      }
      
      if (formData.logo) {
        formDataToSend.append('logo', formData.logo);
      }
      
      if (formData.coach_photo) {
        formDataToSend.append('coach_photo', formData.coach_photo);
      }
      
      if (formData.assistant_coach_photo) {
        formDataToSend.append('assistant_coach_photo', formData.assistant_coach_photo);
      }

      if (editingParticipant) {
        await apiService.admin.updateParticipant(editingParticipant.id, formDataToSend);
        toast({ title: 'Berhasil', description: 'Tersimpan' });
      } else {
        await apiService.admin.createParticipant(formDataToSend);
        toast({ title: 'Berhasil', description: 'Tersimpan' });
      }

      loadParticipants();
      closeModal();
    } catch (error) {
      const errorMsg = error?.response?.data?.detail || 'Gagal menyimpan peserta';
      toast({ title: 'Error', description: errorMsg, variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus peserta ini?')) return;
    try {
      await apiService.admin.deleteParticipant(id);
      toast({ title: 'Berhasil', description: 'Peserta berhasil dihapus' });
      loadParticipants();
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal menghapus peserta', variant: 'destructive' });
    }
  };

  const openModal = (participant = null) => {
    if (participant) {
      setEditingParticipant(participant);
      setFormData({
        name: participant.name,
        city: participant.city,
        participant_type: participant.participant_type || 'school',
        coach: participant.coach,
        assistant_coach: participant.assistant_coach || '',
        category: participant.category,
        founded_year: participant.founded_year || participant.foundedYear,
        player_format: participant.player_format || '11v11',
        formation: participant.formation || ((participant.player_format || '11v11') === '7v7' ? '2-3-1' : '4-3-3'),
        logo: null,
        coach_photo: null,
        assistant_coach_photo: null
      });
      // Set coach photo previews from existing data
      if (participant.coach_photo) {
        setCoachPhotoPreview(getImageUrl(participant.coach_photo));
      } else {
        setCoachPhotoPreview(null);
      }
      if (participant.assistant_coach_photo) {
        setAssistantCoachPhotoPreview(getImageUrl(participant.assistant_coach_photo));
      } else {
        setAssistantCoachPhotoPreview(null);
      }
    } else {
      setEditingParticipant(null);
      setFormData({
        name: '',
        city: '',
        participant_type: participantType || 'school',
        coach: '',
        assistant_coach: '',
        category: 'U-14',
        founded_year: new Date().getFullYear(),
        player_format: '11v11',
        formation: '4-3-3',
        logo: null,
        coach_photo: null,
        assistant_coach_photo: null
      });
      setCoachPhotoPreview(null);
      setAssistantCoachPhotoPreview(null);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingParticipant(null);
    // Cleanup photo preview URLs
    if (coachPhotoPreview && coachPhotoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(coachPhotoPreview);
    }
    if (assistantCoachPhotoPreview && assistantCoachPhotoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(assistantCoachPhotoPreview);
    }
    setCoachPhotoPreview(null);
    setAssistantCoachPhotoPreview(null);
  };

  // Players Management
  const openPlayersModal = (participant) => {
    setSelectedParticipant(participant);
    setPlayers((participant.players || []).map(normalizePlayer));
    const format = participant.player_format || '11v11';
    setSelectedPlayerFormat(format);
    const options = FORMATIONS_BY_FORMAT[format] || FORMATIONS_BY_FORMAT['11v11'];
    setSelectedFormation(options.includes(participant.formation) ? participant.formation : options[0]);
    resetPlayerForm();
    setShowPlayersModal(true);
  };

  const closePlayersModal = () => {
    setShowPlayersModal(false);
    setSelectedParticipant(null);
    setPlayers([]);
    setSelectedPlayerIndex(null);
    resetPlayerForm();
    // Cleanup photo preview URL
    if (playerPhotoPreview && playerPhotoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(playerPhotoPreview);
    }
  };

  const buildPlayersFormData = (playersToPersist) => {
    const formDataToSend = new FormData();
    formDataToSend.append('name', selectedParticipant?.name || '');
    formDataToSend.append('city', selectedParticipant?.city || '');
    formDataToSend.append('participant_type', selectedParticipant?.participant_type || participantType || 'school');
    formDataToSend.append('coach', selectedParticipant?.coach || '');
    formDataToSend.append('assistant_coach', selectedParticipant?.assistant_coach || '');
    formDataToSend.append('category', selectedParticipant?.category || 'U-14');

    const foundedYear = selectedParticipant?.founded_year || selectedParticipant?.foundedYear;
    if (Number.isFinite(Number(foundedYear))) {
      formDataToSend.append('founded_year', String(foundedYear));
    }

    formDataToSend.append('player_format', selectedPlayerFormat);
    formDataToSend.append('formation', selectedFormation);

    const playersPayload = playersToPersist.map((player) => {
      const {
        photo_upload,
        birth_certificate_upload,
        nisn_upload,
        family_card_upload,
        ...playerData
      } = player;
      return playerData;
    });

    formDataToSend.append('players', JSON.stringify(playersPayload));

    playersToPersist.forEach((player, index) => {
      if (player.photo_upload instanceof File) {
        formDataToSend.append(`player_photo_${index}`, player.photo_upload);
      }
      if (player.birth_certificate_upload instanceof File) {
        formDataToSend.append(`player_birth_certificate_${index}`, player.birth_certificate_upload);
      }
      if (player.nisn_upload instanceof File) {
        formDataToSend.append(`player_nisn_${index}`, player.nisn_upload);
      }
      if (player.family_card_upload instanceof File) {
        formDataToSend.append(`player_family_card_${index}`, player.family_card_upload);
      }
    });

    return formDataToSend;
  };

  const persistPlayers = async (playersToPersist, { successDescription, closeAfterSave = false } = {}) => {
    const formDataToSend = buildPlayersFormData(playersToPersist);
    await apiService.admin.updateParticipant(selectedParticipant.id, formDataToSend);
    setPlayers(playersToPersist.map(normalizePlayer));
    setSelectedParticipant((prev) => (prev ? {
      ...prev,
      players: playersToPersist,
      player_format: selectedPlayerFormat,
      formation: selectedFormation,
    } : prev));
    await loadParticipants();
    toast({ title: 'Berhasil', description: successDescription || 'Data pemain berhasil disimpan' });
    if (closeAfterSave) {
      closePlayersModal();
    }
  };

  const addOrUpdatePlayer = async () => {
    const requiredFields = [
      newPlayer.name,
      newPlayer.number,
      newPlayer.birth_place,
      newPlayer.birth_date,
    ];

    if (requiredFields.some((value) => !value)) {
      toast({ title: 'Error', description: 'Nama, nomor punggung, tempat lahir, dan tanggal lahir wajib diisi', variant: 'destructive' });
      return;
    }

    const shirtNumber = Number(newPlayer.number);
    if (!Number.isInteger(shirtNumber) || shirtNumber < 1 || shirtNumber > 99) {
      toast({ title: 'Error', description: 'Nomor punggung harus angka 1-99', variant: 'destructive' });
      return;
    }
    
    const playerExists = players.some((p, idx) => idx !== editingPlayerIndex && Number(p.number) === shirtNumber);
    if (playerExists) {
      toast({ title: 'Error', description: 'Nomor punggung sudah digunakan', variant: 'destructive' });
      return;
    }

    const playerPayload = {
      name: newPlayer.name.trim(), 
      number: shirtNumber, 
      position: newPlayer.position,
      birth_place: newPlayer.birth_place.trim(),
      birth_date: newPlayer.birth_date,
      gender: newPlayer.gender,
      school_origin: (newPlayer.school_origin || '').trim(),
      class_name: (newPlayer.class_name || '').trim(),
      photo: newPlayer.photo || '',
      birth_certificate_file: newPlayer.birth_certificate_file || '',
      nisn_file: newPlayer.nisn_file || '',
      family_card_file: newPlayer.family_card_file || '',
      photo_upload: newPlayer.photo_upload,
      birth_certificate_upload: newPlayer.birth_certificate_upload,
      nisn_upload: newPlayer.nisn_upload,
      family_card_upload: newPlayer.family_card_upload,
    };

    const nextPlayers = editingPlayerIndex !== null
      ? players.map((player, idx) => (idx === editingPlayerIndex ? playerPayload : player))
      : [...players, playerPayload];

    try {
      await persistPlayers(nextPlayers, {
        successDescription: editingPlayerIndex !== null
          ? 'Data pemain berhasil diupdate'
          : 'Data pemain berhasil ditambahkan',
        closeAfterSave: false,
      });
      resetPlayerForm();
    } catch (error) {
      const errorMsg = error?.response?.data?.detail || 'Gagal menyimpan data pemain';
      toast({ title: 'Error', description: errorMsg, variant: 'destructive' });
    }
  };

  const editPlayer = (index) => {
    const selectedPlayer = players[index];
    if (!selectedPlayer) return;
    setEditingPlayerIndex(index);
    setNewPlayer(normalizePlayer(selectedPlayer));
    setShowAddPlayerForm(true); // Show form when editing
    // Set photo preview from existing photo
    if (selectedPlayer.photo) {
      setPlayerPhotoPreview(getImageUrl(selectedPlayer.photo));
    } else {
      setPlayerPhotoPreview(null);
    }
  };

  const removePlayer = (index) => {
    if (selectedPlayerIndex === index) {
      setSelectedPlayerIndex(null);
    }
    if (selectedPlayerIndex !== null && selectedPlayerIndex > index) {
      setSelectedPlayerIndex(selectedPlayerIndex - 1);
    }
    if (editingPlayerIndex === index) {
      resetPlayerForm();
    }
    if (editingPlayerIndex !== null && editingPlayerIndex > index) {
      setEditingPlayerIndex(editingPlayerIndex - 1);
    }
    setPlayers(players.filter((_, i) => i !== index));
  };

  const savePlayers = async () => {
    try {
      await persistPlayers(players, {
        successDescription: 'Data pemain berhasil disimpan',
        closeAfterSave: true,
      });
    } catch (error) {
      const errorMsg = error?.response?.data?.detail || 'Gagal menyimpan data pemain';
      toast({ title: 'Error', description: errorMsg, variant: 'destructive' });
    }
  };

  const getPositionColor = (position) => {
    const pos = POSITIONS.find(p => p.value === position);
    return pos ? pos.color : 'bg-gray-500';
  };

  const getPositionLabel = (position) => {
    const pos = POSITIONS.find(p => p.value === position);
    return pos ? pos.label : position;
  };

  const groupPlayersByPosition = (playersList) => {
    return {
      GK: playersList.filter(p => p.position === 'GK'),
      DEF: playersList.filter(p => p.position === 'DEF'),
      MID: playersList.filter(p => p.position === 'MID'),
      FWD: playersList.filter(p => p.position === 'FWD'),
    };
  };

  const openDetailPlayerModal = (participantName, player) => {
    setDetailPlayerModal({ open: true, participantName, player });
  };

  const closeDetailPlayerModal = () => {
    setDetailPlayerModal({ open: false, participantName: '', player: null });
  };

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredParticipants = participants.filter((participant) => {
    if (!normalizedSearch) return true;
    const fields = [
      participant.name,
      participant.city,
      participant.coach,
      participant.assistant_coach,
      participant.category,
      participant.player_format,
      participant.formation,
      participant.participant_type,
    ];
    return fields.some((value) => (value || '').toString().toLowerCase().includes(normalizedSearch));
  });

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {participantType === 'club' ? 'Peserta Klub' : participantType === 'school' ? 'Peserta Sekolah' : 'Kelola Peserta'}
            <span className="ml-3 text-lg font-normal text-gray-500">({filteredParticipants.length}/{participants.length})</span>
          </h2>
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-64 px-3 py-2 border rounded-lg"
              placeholder="Cari tim, kota, kategori..."
            />
            {/* View toggle */}
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('card')}
                className={`p-2 transition-colors ${viewMode === 'card' ? 'bg-green-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-100'}`}
                title="Tampilan Thumbnail"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-green-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-100'}`}
                title="Tampilan List"
              >
                <LayoutList className="h-4 w-4" />
              </button>
            </div>
            {!isClubAdmin && (
              <button
                onClick={() => openModal()}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <Plus className="h-5 w-5" />
                <span>Tambah Peserta</span>
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Memuat...</div>
        ) : viewMode === 'list' ? (
          /* ── LIST VIEW WITH PAGINATION ── */
          (() => {
            const totalPages = Math.max(1, Math.ceil(filteredParticipants.length / ITEMS_PER_PAGE));
            const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
            const endIdx = startIdx + ITEMS_PER_PAGE;
            const paginatedParticipants = filteredParticipants.slice(startIdx, endIdx);

            return (
              <div className="space-y-4">
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left px-4 py-3 text-gray-600 font-semibold w-10">No</th>
                          <th className="text-left px-4 py-3 text-gray-600 font-semibold w-12">Logo</th>
                          <th className="text-left px-4 py-3 text-gray-600 font-semibold">Nama Tim</th>
                          <th className="text-left px-4 py-3 text-gray-600 font-semibold">Kota</th>
                          <th className="text-left px-4 py-3 text-gray-600 font-semibold">Jenis</th>
                          <th className="text-left px-4 py-3 text-gray-600 font-semibold">Kategori</th>
                          <th className="text-left px-4 py-3 text-gray-600 font-semibold">Format</th>
                          <th className="text-left px-4 py-3 text-gray-600 font-semibold">Formasi</th>
                          <th className="text-left px-4 py-3 text-gray-600 font-semibold">Pemain</th>
                          <th className="text-left px-4 py-3 text-gray-600 font-semibold">Aksi</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {paginatedParticipants.map((participant, idx) => (
                          <tr key={participant.id} className="hover:bg-gray-50 transition">
                            <td className="px-4 py-3 text-gray-400">{startIdx + idx + 1}</td>
                      <td className="px-4 py-3">
                        <img
                          src={getImageUrl(participant.logo)}
                          alt={participant.name}
                          className="h-10 w-10 object-contain rounded bg-gray-100 p-1"
                        />
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{participant.name}</td>
                      <td className="px-4 py-3 text-gray-600">{participant.city || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${participant.participant_type === 'club' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                          {participant.participant_type === 'club' ? 'Klub' : 'Sekolah'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">{participant.category}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{participant.player_format || '11v11'}</td>
                      <td className="px-4 py-3 text-gray-600">{participant.formation || '-'}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                          {(participant.players || []).length}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openPlayersModal(participant)}
                            className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-2 py-1 rounded text-xs font-medium transition-colors"
                            title="Kelola Pemain"
                          >
                            Pemain
                          </button>
                          <button
                            onClick={() => openModal(participant)}
                            className="bg-blue-100 hover:bg-blue-200 text-blue-700 p-1.5 rounded transition-colors"
                            title="Edit Tim"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          {!isClubAdmin && (
                            <button
                              onClick={() => handleDelete(participant.id)}
                              className="bg-red-100 hover:bg-red-200 text-red-700 p-1.5 rounded transition-colors"
                              title="Hapus Tim"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white rounded-lg shadow-md">
                      <div className="text-sm text-gray-600">
                        Halaman {currentPage} dari {totalPages} | Total {filteredParticipants.length} peserta
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          ← Sebelumnya
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                              currentPage === page
                                ? 'bg-green-600 text-white'
                                : 'border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        ))}
                        <button
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Selanjutnya →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
            );
          })()
        ) : (
          <div className="space-y-4">
            {filteredParticipants.map((participant) => {
              const isExpanded = expandedCard === participant.id;
              const groupedPlayers = groupPlayersByPosition(participant.players || []);
              
              return (
                <div key={participant.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  {/* Main Card */}
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <img 
                          src={getImageUrl(participant.logo)} 
                          alt={participant.name} 
                          className="h-16 w-16 object-contain rounded-lg bg-gray-100 p-2"
                        />
                        <div>
                          <h3 className="font-bold text-lg">{participant.name}</h3>
                          <p className="text-sm text-gray-600">{participant.city}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
                              {participant.category}
                            </span>
                            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                              {participant.formation || '4-3-3'}
                            </span>
                            <span className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-800 rounded-full">
                              {participant.player_format || '11v11'}
                            </span>
                            <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full">
                              {participant.participant_type || 'school'}
                            </span>
                            <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                              {(participant.players || []).length} Pemain
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => openPlayersModal(participant)}
                          className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-3 py-2 rounded-lg flex items-center space-x-1 transition-colors"
                          title="Kelola Pemain"
                        >
                          <Users className="h-4 w-4" />
                          <span className="text-sm">Pemain</span>
                        </button>
                        <button 
                          onClick={() => openModal(participant)} 
                          className="bg-blue-100 hover:bg-blue-200 text-blue-700 p-2 rounded-lg transition-colors"
                          title="Edit Tim"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {!isClubAdmin && (
                          <button 
                            onClick={() => handleDelete(participant.id)} 
                            className="bg-red-100 hover:bg-red-200 text-red-700 p-2 rounded-lg transition-colors"
                            title="Hapus Tim"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setExpandedCard(isExpanded ? null : participant.id)}
                          className="bg-gray-100 hover:bg-gray-200 text-gray-700 p-2 rounded-lg transition-colors"
                        >
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t bg-gray-50 p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-600">Pelatih: <span className="font-medium text-gray-900">{participant.coach}</span></p>
                          {participant.assistant_coach && (
                            <p className="text-sm text-gray-600">Asisten: <span className="font-medium text-gray-900">{participant.assistant_coach}</span></p>
                          )}
                          <p className="text-sm text-gray-600">Tahun Berdiri: <span className="font-medium text-gray-900">{participant.founded_year || participant.foundedYear}</span></p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Jenis Peserta: <span className="font-medium text-gray-900">{participant.participant_type || 'school'}</span></p>
                          <p className="text-sm text-gray-600">Format Tim: <span className="font-medium text-gray-900">{participant.player_format || '11v11'}</span></p>
                          <p className="text-sm text-gray-600">Formasi: <span className="font-medium text-gray-900">{participant.formation || '4-3-3'}</span></p>
                        </div>
                      </div>

                      {/* Players List */}
                      <div className="mt-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Daftar Pemain</h4>
                        {(participant.players || []).length === 0 ? (
                          <p className="text-gray-500 text-sm">Belum ada pemain. Klik tombol "Pemain" untuk menambahkan.</p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {POSITIONS.map(pos => (
                              <div key={pos.value}>
                                <h5 className={`text-sm font-medium text-white ${pos.color} px-2 py-1 rounded mb-2`}>
                                  {pos.label} ({groupedPlayers[pos.value]?.length || 0})
                                </h5>
                                <div className="space-y-1">
                                  {groupedPlayers[pos.value]?.map((player, idx) => (
                                    <button
                                      key={idx}
                                      type="button"
                                      onClick={() => openDetailPlayerModal(participant.name, player)}
                                      className="w-full text-sm bg-white p-2 rounded border flex justify-between items-center hover:bg-blue-50 hover:border-blue-300 transition-colors text-left"
                                    >
                                      <span className="font-medium text-blue-700">{player.name}</span>
                                      <span className="font-bold text-gray-600">#{player.number}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {filteredParticipants.length === 0 && !loading && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-500">
              {participants.length === 0
                ? 'Belum ada peserta. Klik tombol "Tambah Peserta" untuk menambahkan.'
                : 'Peserta tidak ditemukan. Coba kata kunci lain.'}
            </p>
          </div>
        )}

        {/* Player Detail Modal (from expanded participant card) */}
        {detailPlayerModal.open && detailPlayerModal.player && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Detail Pemain</h3>
                  <p className="text-sm text-gray-600">{detailPlayerModal.participantName}</p>
                </div>
                <button onClick={closeDetailPlayerModal} className="text-gray-500 hover:text-gray-700" type="button">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0 flex justify-center">
                  {detailPlayerModal.player.photo ? (
                    <img
                      src={getImageUrl(detailPlayerModal.player.photo)}
                      alt={detailPlayerModal.player.name}
                      className="w-40 h-40 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-40 h-40 rounded-full bg-gray-200 flex items-center justify-center border-4 border-white shadow-lg">
                      <Camera className="w-14 h-14 text-gray-400" />
                    </div>
                  )}
                </div>

                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <p><span className="font-medium">Nama:</span> {detailPlayerModal.player.name || '-'}</p>
                  <p><span className="font-medium">No. Punggung:</span> #{detailPlayerModal.player.number || '-'}</p>
                  <p><span className="font-medium">Posisi:</span> {getPositionLabel(detailPlayerModal.player.position)}</p>
                  <p><span className="font-medium">Jenis Kelamin:</span> {detailPlayerModal.player.gender === 'L' ? 'Laki-laki' : detailPlayerModal.player.gender === 'P' ? 'Perempuan' : '-'}</p>
                  <p><span className="font-medium">Tempat Lahir:</span> {detailPlayerModal.player.birth_place || '-'}</p>
                  <p><span className="font-medium">Tanggal Lahir:</span> {detailPlayerModal.player.birth_date || '-'}</p>
                  <p><span className="font-medium">Asal Sekolah:</span> {detailPlayerModal.player.school_origin || '-'}</p>
                  <p><span className="font-medium">Kelas:</span> {detailPlayerModal.player.class_name || '-'}</p>
                </div>
              </div>

              <div className="mt-6 border-t pt-4">
                <h4 className="font-semibold text-gray-900 mb-3">Dokumen Pemain</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div className="bg-gray-50 border rounded-md p-3">
                    <p className="font-medium text-gray-700 mb-2">Akta Kelahiran</p>
                    {detailPlayerModal.player.birth_certificate_file ? (
                      <a
                        href={getImageUrl(detailPlayerModal.player.birth_certificate_file)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Lihat Dokumen
                      </a>
                    ) : (
                      <p className="text-gray-500">Belum diupload</p>
                    )}
                  </div>

                  <div className="bg-gray-50 border rounded-md p-3">
                    <p className="font-medium text-gray-700 mb-2">Berkas NISN</p>
                    {detailPlayerModal.player.nisn_file ? (
                      <a
                        href={getImageUrl(detailPlayerModal.player.nisn_file)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Lihat Dokumen
                      </a>
                    ) : (
                      <p className="text-gray-500">Belum diupload</p>
                    )}
                  </div>

                  <div className="bg-gray-50 border rounded-md p-3">
                    <p className="font-medium text-gray-700 mb-2">Kartu Keluarga</p>
                    {detailPlayerModal.player.family_card_file ? (
                      <a
                        href={getImageUrl(detailPlayerModal.player.family_card_file)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Lihat Dokumen
                      </a>
                    ) : (
                      <p className="text-gray-500">Belum diupload</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Participant Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {editingParticipant ? 'Edit Peserta' : 'Tambah Peserta'}
              </h3>
              
              {/* Coach Photos Preview */}
              <div className="flex justify-center gap-6 mb-6">
                <div className="text-center">
                  <p className="text-xs font-medium text-gray-600 mb-2">Foto Pelatih</p>
                  {coachPhotoPreview ? (
                    <img 
                      src={coachPhotoPreview} 
                      alt="Foto Pelatih" 
                      className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center border-4 border-white shadow-lg">
                      <Camera className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="text-center">
                  <p className="text-xs font-medium text-gray-600 mb-2">Foto Asisten</p>
                  {assistantCoachPhotoPreview ? (
                    <img 
                      src={assistantCoachPhotoPreview} 
                      alt="Foto Asisten Pelatih" 
                      className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center border-4 border-white shadow-lg">
                      <Camera className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nama Tim</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kota</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pelatih</label>
                    <input
                      type="text"
                      value={formData.coach}
                      onChange={(e) => setFormData({ ...formData, coach: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Asisten Pelatih</label>
                    <input
                      type="text"
                      value={formData.assistant_coach}
                      onChange={(e) => setFormData({ ...formData, assistant_coach: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Foto Pelatih {editingParticipant && '(opsional)'}
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setFormData({ ...formData, coach_photo: file });
                        if (file) {
                          const previewUrl = URL.createObjectURL(file);
                          setCoachPhotoPreview(previewUrl);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Foto Asisten Pelatih {editingParticipant && '(opsional)'}
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setFormData({ ...formData, assistant_coach_photo: file });
                        if (file) {
                          const previewUrl = URL.createObjectURL(file);
                          setAssistantCoachPhotoPreview(previewUrl);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Jenis Peserta</label>
                    <select
                      value={formData.participant_type}
                      onChange={(e) => setFormData({ ...formData, participant_type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                      required
                    >
                      <option value="school">Sekolah</option>
                      <option value="club">Klub</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Kategori</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                      required
                    >
                      <option value="U-12">U-12</option>
                      <option value="U-14">U-14</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Jumlah Pemain / Format</label>
                    <select
                      value={formData.player_format}
                      onChange={(e) => {
                        const nextFormat = e.target.value;
                        const nextFormations = FORMATIONS_BY_FORMAT[nextFormat] || FORMATIONS_BY_FORMAT['11v11'];
                        setFormData((prev) => ({
                          ...prev,
                          player_format: nextFormat,
                          formation: nextFormations.includes(prev.formation) ? prev.formation : nextFormations[0],
                        }));
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                      required
                    >
                      <option value="11v11">11 vs 11</option>
                      <option value="7v7">7 vs 7</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Formasi</label>
                    <select
                      value={formData.formation}
                      onChange={(e) => setFormData({ ...formData, formation: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                      required
                    >
                      {(FORMATIONS_BY_FORMAT[formData.player_format] || FORMATIONS_BY_FORMAT['11v11']).map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tahun Berdiri</label>
                  <input
                    type="number"
                    value={formData.founded_year}
                    onChange={(e) => setFormData({ ...formData, founded_year: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    required
                    min="1900"
                    max={new Date().getFullYear()}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo Tim (opsional)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFormData({ ...formData, logo: e.target.files[0] })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md transition-colors"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors"
                  >
                    {editingParticipant ? 'Update' : 'Simpan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Players Management Modal */}
        {showPlayersModal && selectedParticipant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  Kelola Pemain - {selectedParticipant.name}
                </h3>
                <button onClick={closePlayersModal} className="text-gray-500 hover:text-gray-700">
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Team Format & Formation Selector */}
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h4 className="font-semibold text-gray-900 mb-3">Format Tim & Formasi</h4>
                <div className="flex gap-2 mb-3">
                  {Object.keys(PLAYER_FORMAT_META).map((fmt) => (
                    <button
                      key={fmt}
                      type="button"
                      onClick={() => {
                        const options = FORMATIONS_BY_FORMAT[fmt] || FORMATIONS_BY_FORMAT['11v11'];
                        setSelectedPlayerFormat(fmt);
                        setSelectedFormation((prev) => (options.includes(prev) ? prev : options[0]));
                      }}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectedPlayerFormat === fmt
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-indigo-50'
                      }`}
                    >
                      {PLAYER_FORMAT_META[fmt].label}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {(FORMATIONS_BY_FORMAT[selectedPlayerFormat] || FORMATIONS_BY_FORMAT['11v11']).map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setSelectedFormation(f)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectedFormation === f
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-blue-50'
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Format: <span className="font-bold text-indigo-600">{PLAYER_FORMAT_META[selectedPlayerFormat]?.label || selectedPlayerFormat}</span> | Formasi: <span className="font-bold text-blue-600">{selectedFormation}</span>
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Batas pemain aktif untuk format ini: <span className="font-bold text-emerald-700">Tanpa batas</span>
                </p>
              </div>

              {/* Players List */}
              <div className="mb-4">
                <h4 className="font-semibold text-gray-900 mb-3">Daftar Pemain ({players.length})</h4>
                {players.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Belum ada pemain</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {players
                      .map((player, originalIndex) => ({ ...player, originalIndex }))
                      .sort((a, b) => {
                        const posOrder = { GK: 0, DEF: 1, MID: 2, FWD: 3 };
                        return posOrder[a.position] - posOrder[b.position] || a.number - b.number;
                      })
                      .map((player) => (
                        <div key={`${player.number}-${player.originalIndex}`}>
                          <div 
                            className={`flex items-center justify-between border rounded-lg p-3 transition-colors ${selectedPlayerIndex === player.originalIndex ? 'bg-blue-50 border-blue-300' : 'bg-white'}`}
                          >
                            <button
                              type="button"
                              onClick={() => setSelectedPlayerIndex(selectedPlayerIndex === player.originalIndex ? null : player.originalIndex)}
                              className="space-y-1 text-left flex-1"
                              title="Klik untuk lihat detail pemain"
                            >
                              <div className="flex items-center space-x-3">
                                <span className={`${getPositionColor(player.position)} text-white text-xs px-2 py-1 rounded font-medium`}>
                                  {player.position}
                                </span>
                                <span className="font-bold text-gray-700">#{player.number}</span>
                                <span className="text-gray-900 font-medium">{player.name}</span>
                              </div>
                              <div className="text-xs text-gray-600">
                                {player.birth_place || '-'}, {player.birth_date || '-'} | {player.gender === 'L' ? 'Laki-laki' : player.gender === 'P' ? 'Perempuan' : '-'}
                              </div>
                              <div className="text-xs text-gray-600">
                                Sekolah: {player.school_origin || '-'} | Kelas: {player.class_name || '-'}
                              </div>
                              <div className="text-xs text-blue-700">Klik pemain untuk lihat detail lengkap</div>
                            </button>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => editPlayer(player.originalIndex)}
                                className="text-blue-600 hover:text-blue-800 p-1"
                                title="Edit Pemain"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => removePlayer(player.originalIndex)}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Hapus Pemain"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>

                          {/* Detail Pemain Inline - appears below clicked player */}
                          {selectedPlayerIndex === player.originalIndex && (
                            <div className="mt-2 ml-4 mr-4 border rounded-lg p-4 bg-blue-50">
                              <div className="flex items-center gap-2 mb-3">
                                <User className="h-4 w-4 text-blue-700" />
                                <h5 className="font-semibold text-blue-900">Detail Pemain</h5>
                              </div>
                              
                              {/* Photo and Details Layout */}
                              <div className="flex flex-col md:flex-row gap-4">
                                {/* Player Photo */}
                                <div className="flex-shrink-0 flex justify-center md:justify-start">
                                  {player.photo ? (
                                    <img 
                                      src={getImageUrl(player.photo)} 
                                      alt={player.name}
                                      className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                                    />
                                  ) : (
                                    <div className="w-32 h-32 rounded-full bg-gray-300 flex items-center justify-center border-4 border-white shadow-lg">
                                      <Camera className="w-12 h-12 text-gray-500" />
                                    </div>
                                  )}
                                </div>
                                
                                {/* Player Details */}
                                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                  <p><span className="font-medium">Nama:</span> {player.name || '-'}</p>
                                  <p><span className="font-medium">No. Punggung:</span> #{player.number || '-'}</p>
                                  <p><span className="font-medium">Posisi:</span> {getPositionLabel(player.position)}</p>
                                  <p><span className="font-medium">Jenis Kelamin:</span> {player.gender === 'L' ? 'Laki-laki' : player.gender === 'P' ? 'Perempuan' : '-'}</p>
                                  <p><span className="font-medium">Tempat Lahir:</span> {player.birth_place || '-'}</p>
                                  <p><span className="font-medium">Tanggal Lahir:</span> {player.birth_date || '-'}</p>
                                  <p><span className="font-medium">Asal Sekolah:</span> {player.school_origin || '-'}</p>
                                  <p><span className="font-medium">Kelas:</span> {player.class_name || '-'}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Add Player Button and Form */}
              <div className="mb-4">
                {!showAddPlayerForm ? (
                  <button
                    onClick={() => setShowAddPlayerForm(true)}
                    className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                  >
                    <Plus className="h-5 w-5" />
                    <span className="font-medium">Tambah Pemain Baru</span>
                  </button>
                ) : (
                  <div className="bg-gray-50 p-4 rounded-lg border-2 border-green-500">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold text-gray-900">
                        {editingPlayerIndex !== null ? 'Edit Data Pemain' : 'Tambah Pemain Baru'}
                      </h4>
                      <button
                        onClick={resetPlayerForm}
                        className="text-gray-500 hover:text-gray-700"
                        title="Tutup Form"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    
                    {/* Photo Preview - only show if there's a photo */}
                    {playerPhotoPreview && (
                      <div className="flex justify-center mb-4">
                        <div className="relative">
                          <img 
                            src={playerPhotoPreview} 
                            alt="Preview" 
                            className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                          />
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <input
                          type="text"
                          placeholder="Nama Pemain"
                          value={newPlayer.name}
                          onChange={(e) => setNewPlayer({ ...newPlayer, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          placeholder="No. Punggung"
                          value={newPlayer.number}
                          onChange={(e) => setNewPlayer({ ...newPlayer, number: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                          min="1"
                          max="99"
                        />
                      </div>
                      <div>
                        <select
                          value={newPlayer.position}
                          onChange={(e) => setNewPlayer({ ...newPlayer, position: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                        >
                          {POSITIONS.map(pos => (
                            <option key={pos.value} value={pos.value}>{pos.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <select
                          value={newPlayer.gender}
                          onChange={(e) => setNewPlayer({ ...newPlayer, gender: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                        >
                          <option value="">Jenis Kelamin</option>
                          {PLAYER_GENDERS.map((gender) => (
                            <option key={gender.value} value={gender.value}>{gender.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <input
                          type="text"
                          placeholder="Tempat Lahir"
                          value={newPlayer.birth_place}
                          onChange={(e) => setNewPlayer({ ...newPlayer, birth_place: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                      <div>
                        <input
                          type="date"
                          value={newPlayer.birth_date}
                          onChange={(e) => setNewPlayer({ ...newPlayer, birth_date: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          placeholder="Asal Sekolah"
                          value={newPlayer.school_origin}
                          onChange={(e) => setNewPlayer({ ...newPlayer, school_origin: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          placeholder="Kelas"
                          value={newPlayer.class_name}
                          onChange={(e) => setNewPlayer({ ...newPlayer, class_name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Foto Pemain <span className="text-gray-400">(opsional)</span></label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            setNewPlayer({ ...newPlayer, photo_upload: file });
                            // Create preview URL
                            if (file) {
                              const previewUrl = URL.createObjectURL(file);
                              setPlayerPhotoPreview(previewUrl);
                            } else {
                              setPlayerPhotoPreview(null);
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Akta Kelahiran <span className="text-gray-400">(opsional)</span></label>
                        <input
                          type="file"
                          accept=".pdf,image/*"
                          onChange={(e) => setNewPlayer({ ...newPlayer, birth_certificate_upload: e.target.files?.[0] || null })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Berkas NISN <span className="text-gray-400">(opsional)</span></label>
                        <input
                          type="file"
                          accept=".pdf,image/*"
                          onChange={(e) => setNewPlayer({ ...newPlayer, nisn_upload: e.target.files?.[0] || null })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Kartu Keluarga <span className="text-gray-400">(opsional)</span></label>
                        <input
                          type="file"
                          accept=".pdf,image/*"
                          onChange={(e) => setNewPlayer({ ...newPlayer, family_card_upload: e.target.files?.[0] || null })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                      <button
                        type="button"
                        onClick={addOrUpdatePlayer}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center justify-center space-x-2 transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                        <span>{editingPlayerIndex !== null ? 'Update Data Pemain' : 'Tambah Pemain + Berkas'}</span>
                      </button>
                      <button
                        type="button"
                        onClick={resetPlayerForm}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md transition-colors"
                      >
                        Batal
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Save Button */}
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={closePlayersModal}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md transition-colors"
                >
                  Batal
                </button>
                <button
                  type="button"
                  onClick={savePlayers}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors"
                >
                  Simpan Pemain
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminParticipants;
